import { useMemo, useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { formatDateTime } from '../../../../shared/format.js';
import { useDevices, useDeleteDevice } from '../../hooks/useDevices.js';
import { DeviceDrawer } from './DeviceDrawer.jsx';
import { DeviceLogsModal } from './DeviceLogsModal.jsx';
import { ConfirmModal } from '../shared/ConfirmModal.jsx';

const TYPE_LABEL = {
  rfid_reader: 'قارئ RFID',
  printer: 'طابعة',
  gate: 'بوابة',
  pos: 'POS',
  scanner: 'ماسح'
};

const STATUS_TONE = {
  online: { variant: 'success', label: 'متصل', dot: '#22C55E' },
  offline: { variant: 'muted', label: 'غير متصل', dot: '#6B7280' },
  error: { variant: 'danger', label: 'خطأ', dot: '#EF4444' }
};

export function DevicesTable() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canManage = hasPermission('settings.devices.manage');

  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [logsDevice, setLogsDevice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data: devices = [], isLoading, error } = useDevices();
  const deleteDevice = useDeleteDevice();

  const filtered = useMemo(() => {
    const arr = Array.isArray(devices) ? devices : [];
    return arr.filter((d) => {
      if (typeFilter && d.type !== typeFilter) return false;
      if (statusFilter && d.status !== statusFilter) return false;
      return true;
    });
  }, [devices, typeFilter, statusFilter]);

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }
  function openEdit(d) {
    setEditing(d);
    setDrawerOpen(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="input"
            style={{ minWidth: 160 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">جميع الأنواع</option>
            {Object.entries(TYPE_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="input"
            style={{ minWidth: 140 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">كل الحالات</option>
            <option value="online">متصل</option>
            <option value="offline">غير متصل</option>
            <option value="error">خطأ</option>
          </select>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> جهاز جديد
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل الأجهزة</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 text-sm">لا توجد أجهزة مسجّلة</div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">الاسم</th>
                <th className="p-2.5 text-start">المعرف</th>
                <th className="p-2.5 text-start">النوع</th>
                <th className="p-2.5 text-start">الحالة</th>
                <th className="p-2.5 text-start">الموقع</th>
                <th className="p-2.5 text-start">آخر اتصال</th>
                <th className="p-2.5 text-end">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const id = d._id || d.id;
                const status = STATUS_TONE[d.status] || STATUS_TONE.offline;
                return (
                  <tr key={id} className="border-b border-line">
                    <td className="p-2.5 font-bold">{d.name}</td>
                    <td className="p-2.5 font-mono text-[12px]">{d.deviceId}</td>
                    <td className="p-2.5">
                      <Pill variant="info">{TYPE_LABEL[d.type] || d.type}</Pill>
                    </td>
                    <td className="p-2.5">
                      <Pill variant={status.variant}>
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: status.dot }}
                        />
                        {status.label}
                      </Pill>
                    </td>
                    <td className="p-2.5 text-[12px]">{d.location || '—'}</td>
                    <td className="p-2.5 text-[12px] text-ink-3">
                      {d.lastSeenAt ? formatDateTime(d.lastSeenAt) : '—'}
                    </td>
                    <td className="p-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: '4px 8px' }}
                          onClick={() => setLogsDevice(d)}
                          title="السجل"
                        >
                          <Icon name="reports" size={13} />
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              className="btn"
                              style={{ padding: '4px 8px' }}
                              onClick={() => openEdit(d)}
                              title="تعديل"
                            >
                              <Icon name="edit" size={13} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              style={{ padding: '4px 8px' }}
                              onClick={() => setConfirmDelete(d)}
                              title="حذف"
                            >
                              <Icon name="trash" size={13} />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <DeviceDrawer
        open={drawerOpen}
        device={editing}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />
      <DeviceLogsModal
        open={!!logsDevice}
        device={logsDevice}
        onClose={() => setLogsDevice(null)}
      />
      <ConfirmModal
        open={!!confirmDelete}
        title="حذف جهاز"
        message={`حذف "${confirmDelete?.name || ''}"؟`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteDevice.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteDevice.mutateAsync(confirmDelete._id || confirmDelete.id, {
            onSuccess: () => setConfirmDelete(null)
          });
        }}
      />
    </div>
  );
}
